/* Search pagination owns its request so facet replacements cannot append stale results. */
(() => {
  if (customElements.get('cs-search-pagination')) return;
  class SearchPagination extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('#ProductGridContainer');
      if (!this.container) return;
      this.grid = this.container.querySelector('.product-grid');
      this.button = this.querySelector('[data-search-more]');
      this.status = this.querySelector('[data-search-status]');
      this.pages = this.querySelector('[data-search-pages]');
      this.next = this.dataset.nextUrl;
      this.paused = false;
      this.refreshing = false;
      this.button.hidden = !this.next;
      this.pages.hidden = Boolean(this.next);
      this.click = () => { this.paused = false; this.load(); };
      this.button.addEventListener('click', this.click);
      // Facets adds this class before its request, not just when it replaces HTML.
      const collection = this.container.querySelector('.collection');
      this.mutations = new MutationObserver(() => {
        if (collection.classList.contains('loading')) {
          this.refreshing = true;
          this.controller?.abort();
          this.observer?.disconnect();
          this.button.disabled = true;
        }
      });
      if (collection) this.mutations.observe(collection, { attributes: true, attributeFilter: ['class'] });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.load();
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this.querySelector('[data-search-sentinel]'));
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      this.button?.removeEventListener('click', this.click);
      clearTimeout(this.timer);
    }
    async load() {
      if (!this.isConnected || !this.next || this.loading || this.paused || this.refreshing) return;
      this.loading = true;
      this.button.disabled = true;
      this.status.textContent = 'Loading more results…';
      const controller = this.controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(this.next, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Invalid pagination URL');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || this.refreshing) return;
        const pager = html.querySelector('cs-search-pagination');
        if (!pager || pager.dataset.sectionId !== this.dataset.sectionId ||
            Number(pager.dataset.page) !== Number(new URL(this.next, location.href).searchParams.get('page'))) {
          throw new Error('Unexpected search response');
        }
        const grid = html.querySelector('#ProductGridContainer .product-grid');
        const items = grid ? Array.from(grid.children) : [];
        if (!items.length && Number(pager.dataset.resultCount) !== 0) throw new Error('Missing results');
        if (!items.length && pager.dataset.nextUrl) {
          // Empty pages can occur before Shopify's advertised final page. Advance
          // through a bounded batch, then leave an explicit continuation control.
          this.emptyPages = (this.emptyPages || 0) + 1;
        } else {
          this.emptyPages = 0;
        }
        const next = pager.dataset.nextUrl;
        if (next && Number(new URL(next, location.href).searchParams.get('page')) <= Number(pager.dataset.page)) {
          throw new Error('Non-advancing pagination');
        }
        if (items.length) {
          if (!this.grid) {
            const collection = this.container.querySelector('.collection');
            if (!collection) throw new Error('Missing current results container');
            collection.classList.remove('collection--empty');
            collection.querySelector('.title-wrapper')?.remove();
            this.grid = grid.cloneNode(false);
            collection.appendChild(this.grid);
          }
          const seen = new Set(Array.from(this.grid.children, item => item.dataset.searchResult).filter(Boolean));
          for (const item of items) {
            if (item.dataset.searchResult && seen.has(item.dataset.searchResult)) continue;
            if (item.dataset.searchResult) seen.add(item.dataset.searchResult);
            // Dynamically appended cards must be visible without Dawn reinitialization.
            item.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--slide-in');
            this.grid.appendChild(item);
          }
        }
        this.next = next;
        this.dataset.nextUrl = next;
        this.dataset.page = pager.dataset.page;
        this.pages.innerHTML = pager.querySelector('[data-search-pages]')?.innerHTML || '';
        this.status.textContent = next ? '' : 'All returned results are shown.';
        this.button.hidden = !next;
        this.button.textContent = 'Load more';
        if (!next) {
          this.observer?.disconnect();
          this.pages.hidden = true;
        } else if (this.emptyPages >= 3) {
          this.paused = true;
          this.emptyPages = 0;
          this.pages.hidden = false;
          this.status.textContent = 'No further results returned. You can check for more or refine your search.';
          this.button.textContent = 'Check for more results';
        }
      } catch (error) {
        if (!this.isConnected || this.refreshing) return;
        this.paused = true;
        this.pages.hidden = false;
        this.status.textContent = 'More results could not be loaded. Please retry or use the page links below.';
        this.button.textContent = 'Retry';
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.loading = false;
          this.button.disabled = this.refreshing;
        }
      }
      // Recheck even if the sentinel stayed visible and no new scroll event fired.
      if (this.isConnected && this.next && !this.paused && !this.refreshing && this.observer) {
        this.timer = setTimeout(() => {
          if (this.querySelector('[data-search-sentinel]').getBoundingClientRect().top < innerHeight + 300) this.load();
        }, 0);
      }
    }
  }
  customElements.define('cs-search-pagination', SearchPagination);
})();
